import { type NexusApiActionDef } from "./action-def.types";
import { DOCUMENT_TEMPLATE_KINDS, type DocumentTemplateKind } from "./document-template-kinds";
import { arr, bool, num, obj, str } from "./output-format.builders";

/**
 * What each accepted kind is CALLED in the two pickers.
 *
 * Typed as an exhaustive `Record` over `DocumentTemplateKind`, so adding a kind
 * to `DOCUMENT_TEMPLATE_KIND_TO_TYPE` and forgetting it here is a compile error
 * rather than a dropdown entry that silently never appears. The two doors label
 * the same kinds differently — the list filter reads as a noun phrase, the
 * create picker as a bare name — which is why the labels live here and only the
 * VALUE set is shared.
 */
const TEMPLATE_KIND_FILTER_LABELS: Record<DocumentTemplateKind, string> = {
  WORD: "Word Documents",
  POWERPOINT: "PowerPoint Presentations",
  HTML: "HTML Templates"
};

const TEMPLATE_KIND_CREATE_LABELS: Record<DocumentTemplateKind, string> = {
  WORD: "Word",
  POWERPOINT: "PowerPoint",
  HTML: "HTML"
};

export const documentTemplateActions: NexusApiActionDef[] = [
  {
    action: "documentTemplates.list",
    label: "List Document Templates",
    description: "List all document templates in your organization",
    inputSchema: {
      filterType: {
        type: "string",
        description: "Filter by template type (optional)",
        constraints: {
          allowedValues: ["", ...DOCUMENT_TEMPLATE_KINDS],
          allowedOptions: [
            { label: "All Types", value: "" },
            ...DOCUMENT_TEMPLATE_KINDS.map((kind) => ({
              label: TEMPLATE_KIND_FILTER_LABELS[kind],
              value: kind
            }))
          ]
        }
      }
    },
    outputFormat: obj("Result", {
      templates: arr("List of templates"),
      totalCount: num("Total count")
    })
  },
  {
    action: "documentTemplates.get",
    label: "Get Document Template",
    description: "Get document template details by ID",
    inputSchema: {
      templateId: { type: "string", description: "Template ID" }
    },
    outputFormat: obj("Template details")
  },
  {
    action: "documentTemplates.create",
    label: "Create Document Template",
    description: "Create a new document template",
    inputSchema: {
      name: { type: "string", description: "Template name" },
      type: {
        type: "string",
        description: "Template type",
        constraints: {
          allowedValues: [...DOCUMENT_TEMPLATE_KINDS],
          allowedOptions: DOCUMENT_TEMPLATE_KINDS.map((kind) => ({
            label: TEMPLATE_KIND_CREATE_LABELS[kind],
            value: kind
          }))
        }
      },
      fileUrl: { type: "string", description: "File URL (optional)" },
      description: { type: "string", description: "Description (optional)" }
    },
    outputFormat: obj("Created template")
  },
  {
    action: "documentTemplates.search",
    label: "Search Document Templates",
    description: "Find document templates using AI-powered semantic search",
    inputSchema: {
      query: { type: "string", description: "Search query" },
      limit: {
        type: "number",
        description: "Maximum results",
        constraints: {
          allowedValues: [1, 3, 5, 10],
          allowedOptions: [
            { label: "1 result", value: 1 },
            { label: "3 results", value: 3 },
            { label: "5 results", value: 5 },
            { label: "10 results", value: 10 }
          ]
        }
      },
      similarityThreshold: { type: "number", description: "Minimum similarity (0-1, optional)" },
      includeInputFormat: { type: "boolean", description: "Include template variables (optional)" }
    },
    outputFormat: obj("Search results", {
      found: bool("Whether matches were found"),
      templates: arr("Matching templates"),
      count: num("Number of results")
    })
  },
  {
    action: "documentTemplates.generate",
    label: "Generate Document",
    description: "Generate a document from a template with data",
    inputSchema: {
      templateId: { type: "string", description: "Template ID" },
      dataInput: { type: "string", description: "Template data as JSON string" }
    },
    outputFormat: obj("Generated document", {
      fileUrl: str("Download URL"),
      type: str("Document type"),
      templateId: str("Template ID"),
      templateName: str("Template name"),
      generatedAt: str("Generation timestamp")
    })
  }
];
